/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { AllProductSupplierOrgs, ApiOrgProduct } from '@kaiizen/kai-pro-api';
import { useAllProductSupplierOrgs } from '@kaiizen/kai-pro-api-react-state';
import { useRef, useState } from 'react';
import { formatUsd } from '../Presenters';
import { LAYOUT2, TYPOGRAPHY2 } from '../Styles';

const COPIED_REVERT_MS = 1000;

const BODY_STYLE = css`
  label: ORG_PRODUCT_DETAILS_BODY;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing1p5}px;
`;

const PRODUCT_IMAGE_STYLE = css`
  label: PRODUCT_IMAGE_STYLE;
  width: 100%;
  aspect-ratio: 1;
  object-fit: contain;
  background: var(--background2);
  border: 1px solid var(--border2);
`;

const MISSING_IMAGE_STYLE = css`
  label: MISSING_IMAGE_STYLE;
  width: 100%;
  aspect-ratio: 1;
  background: var(--background2);
  border: 1px solid var(--border2);
  display: flex;
  align-items: center;
  justify-content: center;
  ${TYPOGRAPHY2.small}
  color: var(--text2);
  font-style: italic;
`;

const TITLE_ROW_STYLE = css`
  label: TITLE_ROW_STYLE;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${LAYOUT2.spacing1}px;
`;

const PRODUCT_TITLE_STYLE = css`
  label: PRODUCT_TITLE_STYLE;
  ${TYPOGRAPHY2.large}
  color: var(--text);
  flex: 1;
  word-wrap: break-word;
`;

const PRICE_STYLE = css`
  label: PRICE_STYLE;
  ${TYPOGRAPHY2.largeBold}
  color: var(--textGreen);
`;

const SKU_SECTION_STYLE = css`
  label: SKU_SECTION_STYLE;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacingHalf}px;
  padding: ${LAYOUT2.spacing1p5}px;
  background: var(--background2);
  border: 1px solid var(--border2);
`;

const SKU_ROW_STYLE = css`
  label: SKU_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
  min-height: 30px;
`;

const SKU_ROW_COPYABLE_STYLE = css`
  ${SKU_ROW_STYLE}
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  width: 100%;

  &:hover .copy-action {
    text-decoration: underline;
  }
`;

const SKU_LABEL_STYLE = css`
  label: SKU_LABEL_STYLE;
  ${TYPOGRAPHY2.smallBold}
  color: var(--text2);
  min-width: 100px;
`;

const SKU_VALUE_STYLE = css`
  label: SKU_VALUE_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text);
  flex: 1;
  word-wrap: break-word;
`;

const COPY_ACTION_STYLE = css`
  label: COPY_ACTION_STYLE;
  ${TYPOGRAPHY2.smallBold}
  color: var(--textGreen);
`;

const DESCRIPTION_STYLE = css`
  label: DESCRIPTION_STYLE;
  ${TYPOGRAPHY2.medium}
  color: var(--text);
  line-height: 1.5;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const findProductSupplierOrgName = (
  productSupplierOrgId: string | undefined | null,
  productSupplierOrgs: AllProductSupplierOrgs | undefined,
): string | undefined => {
  if (!productSupplierOrgId || !productSupplierOrgs) return undefined;
  for (const org of Object.values(productSupplierOrgs)) {
    if (org?.id === productSupplierOrgId) return org.name;
  }
  return undefined;
};

const SupplierOrgRow = ({ name }: { name?: string }) => {
  if (!name) return null;
  return (
    <div css={SKU_ROW_STYLE}>
      <div css={SKU_LABEL_STYLE}>Supplier:</div>
      <div css={SKU_VALUE_STYLE}>{name}</div>
    </div>
  );
};

const CopyableSkuRow = ({ label, value }: { label: string; value?: string | null }) => {
  const [justCopied, setJustCopied] = useState(false);
  const revertTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  if (!value) return null;

  const onClick = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      console.log({ clipboardError: error });
      return;
    }
    setJustCopied(true);
    if (revertTimeoutRef.current) clearTimeout(revertTimeoutRef.current);
    revertTimeoutRef.current = setTimeout(() => setJustCopied(false), COPIED_REVERT_MS);
  };

  return (
    <button type='button' onClick={onClick} css={SKU_ROW_COPYABLE_STYLE}>
      <div css={SKU_LABEL_STYLE}>{label}:</div>
      <div css={SKU_VALUE_STYLE}>{value}</div>
      <div className='copy-action' css={COPY_ACTION_STYLE}>{justCopied ? 'copied' : 'copy'}</div>
    </button>
  );
};

export const OrgProductDetails = ({
  orgProduct,
}: {
  orgProduct: ApiOrgProduct | null;
}) => {
  const { productSupplierOrgs } = useAllProductSupplierOrgs();
  if (!orgProduct) return null;
  const supplierOrgName = findProductSupplierOrgName(orgProduct.productSupplierOrgId, productSupplierOrgs);

  return (
    <div css={BODY_STYLE}>
      {orgProduct.image
        ? <img src={orgProduct.image} alt={orgProduct.title || 'Product image'} css={PRODUCT_IMAGE_STYLE} />
        : <div css={MISSING_IMAGE_STYLE}>No image</div>}

      <div css={TITLE_ROW_STYLE}>
        {orgProduct.title && (
          <div css={PRODUCT_TITLE_STYLE}>
            {orgProduct.title}
            {orgProduct.brand ? ` (${orgProduct.brand})` : ''}
          </div>
        )}
        {orgProduct.price != null && <div css={PRICE_STYLE}>{formatUsd(orgProduct.price)}</div>}
      </div>

      <div css={SKU_SECTION_STYLE}>
        <SupplierOrgRow name={supplierOrgName} />
        <CopyableSkuRow label='SKU' value={orgProduct.sku} />
        <CopyableSkuRow label='Store SKU' value={orgProduct.thdStoreSku} />
      </div>

      {orgProduct.description && <div css={DESCRIPTION_STYLE}>{orgProduct.description}</div>}
    </div>
  );
};
